import {
  Admin,
  AuthResponse,
  Lead,
  LeadsResponse,
  StatsResponse,
  LeadFilters
} from '../types';

// Use environment variables for the API URL for flexibility
const API_URL = process.env.REACT_APP_BACKEND_URL || 'https://api.askfinancee.com/api';



// Define a generic type for a successful deletion or update response
interface SuccessMessage {
  message: string;
}

class ApiService {
  /**
   * A private helper method to handle all fetch requests.
   * It centralizes headers, error handling, and JSON parsing.
   * @param endpoint The API endpoint to call (e.g., '/admin/login').
   * @param options The RequestInit options for fetch (method, body, etc.).
   * @param token Optional JWT token for authorized requests.
   * @returns The JSON response from the server.
   */
  private async _request<T>(endpoint: string, options: RequestInit = {}, token?: string): Promise<T> {
    // This headers object is built conditionally to include Authorization only when a token is present.
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
      ...(token && { 'Authorization': `Bearer ${token}` }),
      ...options.headers,
    };

    const response = await fetch(`${API_URL}${endpoint}`, { ...options, headers });

    if (!response.ok) {
      let errorMessage = `Request failed with status ${response.status}`;
      try {
        // Try to parse a JSON error response from the server
        const errorData = await response.json();
        errorMessage = errorData.message || errorMessage;
      } catch (e) {
        // Ignore if the error response is not JSON
      }
      throw new Error(errorMessage);
    }
    
    // For methods like DELETE that might not return a body (status 204 No Content)
    if (response.status === 204) {
        return {} as T;
    }

    return response.json();
  }

  // --- Auth Endpoints ---

  async register(fullName: string, email: string, password: string, confirmPassword: string): Promise<AuthResponse> {
    return this._request('/admin/register', {
      method: 'POST',
      body: JSON.stringify({ fullName, email, password, confirmPassword }),
    });
  }

  async login(email: string, password: string): Promise<AuthResponse> {
    return this._request('/admin/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    });
  }

  async logout(token: string): Promise<void> {
    await this._request('/admin/logout', { method: 'POST' }, token);
  }

  async getProfile(token: string): Promise<Admin> {
    return this._request('/admin/profile', {}, token);
  }

  // --- Leads Endpoints ---

  async getLeads(token: string, filters: LeadFilters = {}): Promise<LeadsResponse> {
    // Filter out any undefined or null values before creating search params
    const cleanFilters = Object.fromEntries(
      Object.entries(filters).filter(([, value]) => value != null)
    );
    const params = new URLSearchParams(cleanFilters as Record<string, string>).toString();
    const endpoint = params ? `/leads?${params}` : '/leads';
    return this._request(endpoint, {}, token);
  }

  async getLead(token: string, id: string): Promise<Lead> {
    return this._request(`/leads/${id}`, {}, token);
  }

  async createLead(token: string, data: Partial<Lead>): Promise<Lead> {
    return this._request('/leads', { method: 'POST', body: JSON.stringify(data) }, token);
  }

  async updateLead(token: string, id: string, data: Partial<Lead>): Promise<Lead> {
    return this._request(`/leads/${id}`, { method: 'PUT', body: JSON.stringify(data) }, token);
  }

  async deleteLead(token: string, id: string): Promise<SuccessMessage> {
    return this._request(`/leads/${id}`, { method: 'DELETE' }, token);
  }

  async getStats(token: string): Promise<StatsResponse> {
    return this._request('/leads/stats/overview', {}, token);
  }

  // --- Bulk Actions ---

  async bulkUpdateStatus(token: string, leadIds: string[], status: string): Promise<SuccessMessage> {
    return this._request('/leads/bulk/status', {
      method: 'PATCH',
      body: JSON.stringify({ leadIds, status }),
    }, token);
  }

  async bulkDelete(token: string, leadIds: string[]): Promise<SuccessMessage> {
    return this._request('/leads/bulk/delete', {
      method: 'DELETE',
      body: JSON.stringify({ leadIds }),
    }, token);
  }
}

// Export a singleton instance of the service
export default new ApiService();